import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Link } from '../../../components/Link';
import { HubCrossLinks } from '../../../components/hub/HubCrossLinks';
import type { HubCrossLink } from '../../../types/hub-cross-link';
import { buildCityJobsLink, buildHubIndexLinks, hubLinksHeading } from '../../../utils/seo/hub-index-links';
import { finlandHubSlugForEmployersCitySlug } from '../../../utils/best-companies/employers-city-slugs';
import { BreadcrumbNavigation } from '../../../components/BreadcrumbNavigation';
import { BestCompaniesCompanyRow } from '../components/BestCompaniesCompanyRow';
import type { Data } from './+data.server';
import {
  BEST_COMPANIES_DIMENSION_SCRIPT_ID,
} from './parseBestCompaniesDimensionInitialScript';
import { useBestCompaniesDimensionPagePayload } from '../../../utils/best-companies/use-best-companies-dimension-page-payload';
import { Msg, useFormatMessage } from '../../../i18n-review/client/Msg';

export default function BestCompaniesDimensionPage() {
  const { data, isLoadingClientFallback } = useBestCompaniesDimensionPagePayload();

  if (!data?.config) {
    if (isLoadingClientFallback) {
      return (
        <div className="min-h-screen bg-jc-page">
          <div className="max-w-3xl mx-auto p-4 pt-2 sm:pt-4 pb-16">
            <div className="animate-pulse space-y-4" aria-busy="true">
              <div className="h-4 w-48 rounded bg-jc-border" />
              <div className="h-8 w-full max-w-md rounded bg-jc-border" />
              <div className="h-20 rounded-xl bg-jc-border/80" />
              <div className="h-20 rounded-xl bg-jc-border/80" />
              <div className="h-20 rounded-xl bg-jc-border/80" />
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  return <BestCompaniesDimensionPageBody data={data} />;
}

function BestCompaniesDimensionPageBody({ data }: { data: Data }) {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { companies, config, displayLabel, dimensionSlug } = data;
  const dimensionTitleLabel = displayLabel ?? config.label;
  const pageLocale = (intl.locale === 'fi' ? 'fi' : intl.locale === 'sv' ? 'sv' : 'en') as 'en' | 'fi' | 'sv';

  return (
    <>
      <script
        id={BEST_COMPANIES_DIMENSION_SCRIPT_ID}
        type="application/json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
      />
      <div className="min-h-screen bg-jc-page">
        <div className="max-w-3xl mx-auto p-4 pt-2 sm:pt-4 pb-16">
          <BreadcrumbNavigation
            items={[
              {
                label: formatMessage({
                  id: 'bestCompanies.breadcrumb.hub',
                  defaultMessage: 'Companies',
                  description: 'Breadcrumb link to best employers hub',
                }),
                href: '/best-companies',
              },
              { label: dimensionTitleLabel },
            ]}
            className="pb-3 text-jc-muted"
            linkClassName="text-jc-muted hover:text-jc-accent transition-colors duration-200"
            chevronClassName="h-4 w-4 mx-1 sm:mx-2 text-jc-contrast-subtle"
            useChevronRightIcon
          />

          <h1 className="text-2xl font-bold text-jc-ink mb-2">
            <Msg
              id="bestCompanies.dimension.title"
              defaultMessage="Employers hiring in {label}"
              values={{ label: dimensionTitleLabel }}
              description="H1 on a best-companies dimension page"
            />
          </h1>
          <p className="text-sm text-jc-muted mb-8 leading-relaxed">
            <Msg
              id="bestCompanies.dimension.introPrefix"
              defaultMessage="Most active hiring first. Each employer also carries its"
              description="Start of methodology sentence on dimension page"
            />{' '}
            <Link
              href="/about-employer-score"
              locale={intl.locale}
              className="text-jc-accent font-medium hover:text-jc-cta-hover underline-offset-2 hover:underline"
            >
              <Msg
                id="bestCompanies.dimension.scoreName"
                defaultMessage="JobCrawls Employer Score"
                description="Link text to employer score docs"
              />
            </Link>{' '}
            <Msg
              id="bestCompanies.dimension.introSuffix"
              defaultMessage=", computed from six signals — pay, remote flexibility, hiring stability, hiring growth, benefits and salary transparency — in listings aimed at the Finnish job market (including many remote roles). Not reviews."
              description="End of methodology on dimension page; scope is Finland-targeted listings"
            />
          </p>

          {companies.length === 0 ? (
            <p className="text-jc-muted">
              <Msg
                id="bestCompanies.dimension.empty"
                defaultMessage="No employers found for this filter yet. Check back after the next index run."
              />
            </p>
          ) : (
            <ol className="space-y-3">
              {companies.map((company, i) => (
                <li key={company.objectID}>
                  <BestCompaniesCompanyRow company={company} rank={i + 1} />
                </li>
              ))}
            </ol>
          )}

          <HubCrossLinks
            variant="chips"
            locale={pageLocale}
            heading={hubLinksHeading(pageLocale)}
            className="mt-10"
            links={[
              ...((): HubCrossLink[] => {
                const hubSlug = config.type === 'city' ? finlandHubSlugForEmployersCitySlug(dimensionSlug) : null;
                return hubSlug ? [buildCityJobsLink(pageLocale, hubSlug, dimensionTitleLabel)] : [];
              })(),
              ...buildHubIndexLinks(pageLocale, ['employers', 'roles', 'salary']),
            ]}
          />

          <div className="mt-10 pt-6 border-t border-jc-border">
            <Link
              href="/best-companies"
              locale={intl.locale}
              className="text-sm text-jc-muted hover:text-jc-accent transition-colors font-medium"
            >
              <Msg
                id="bestCompanies.dimension.backToHub"
                defaultMessage="← All employer rankings"
                description="Link back to best employers hub"
              />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
